import dayjs from 'dayjs'
import { create } from 'zustand'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { DueDateInfoType } from 'components/pages/healthTools/due-date'
import { OtherInfo } from 'components/pages/healthTools/due-date/types'
import {
  API_DATE_FORMAT,
  assetImagePath,
  DISPLAY_DATE_FORMAT,
} from 'interfaces/constants'

export const STORE_KEY = 'duedateInfo'

export type DueDateFormValues = {
  birthday?: Date
  isFirstPregnant: boolean
  isMyself: boolean
  ultrasoundCRL?: number
  ultrasoundMSD?: number
}

export enum DueDateCalculatorType {
  lastPeriod = 0,
  conception = 1,
  IVF = 2,
  IUI = 3,
  ultrasound = 4,
}

enum DueDateMethodAPI {
  lastPeriod = 'lmp',
  conception = 'cd',
  IVF = 'ivf',
  IUI = 'iui',
  ultrasound = 'us',
}

export const DUE_DATE_CONFIG = {
  [String(DueDateCalculatorType.lastPeriod)]: {
    value: String(DueDateCalculatorType.lastPeriod),
    imageSrc: `${assetImagePath}/due-date-last-period-icon-revamp.png`,
    label: 'dueDatePage.lastPeriod',
  },
  [String(DueDateCalculatorType.IVF)]: {
    value: String(DueDateCalculatorType.IVF),
    imageSrc: `${assetImagePath}/due-date-ivf-icon.png`,
    label: 'dueDatePage.ivf',
  },
  [String(DueDateCalculatorType.conception)]: {
    value: String(DueDateCalculatorType.conception),
    imageSrc: `${assetImagePath}/due-date-conception-date-icon.png`,
    label: 'dueDatePage.conceptionDate',
  },
  [String(DueDateCalculatorType.IUI)]: {
    value: String(DueDateCalculatorType.IUI),
    imageSrc: `${assetImagePath}/due-date-iui-icon.png`,
    label: 'dueDatePage.IUI',
  },
  [String(DueDateCalculatorType.ultrasound)]: {
    value: String(DueDateCalculatorType.ultrasound),
    imageSrc: `${assetImagePath}/due-date-ultrasound-icon.png`,
    label: 'dueDatePage.ultrasound',
  },
}

export const getDueDateLastPeriod = async (
  lastPeriodDate: Date,
  additionalInfo: string,
): Promise<DueDateInfoType> => {
  const periodLength = Number(additionalInfo)
  const params = [
    `method=${DueDateMethodAPI.lastPeriod}`,
    `circle_length=${periodLength}`,
    `date=${dayjs(lastPeriodDate).format(DISPLAY_DATE_FORMAT)}`,
  ].join('&')
  const path = `${PATHS.HEALTH_TOOL.GET_DUE_DATE_RESULT}?${params}`
  const otherInfo: OtherInfo = {
    method: DueDateCalculatorType.lastPeriod + 1, // zero index -> 1 based index
    first_day: dayjs(lastPeriodDate).format(API_DATE_FORMAT),
    cycle_length_days: periodLength,
    conception_date: null,
    ivf_transfer_date: null,
    days_transfer: null,
  }
  try {
    const { data } = await callApiNew(path)

    if (!data?.due_date && !data?.gestational_week?.[1]?.from) {
      return { startDate: null, dueDate: null, otherInfo }
    }

    return {
      startDate: dayjs(
        data.gestational_week[1].from,
        DISPLAY_DATE_FORMAT,
      ).toDate(),
      dueDate: dayjs(data.due_date, DISPLAY_DATE_FORMAT).toDate(),
      otherInfo,
    }
  } catch (error) {
    return { startDate: null, dueDate: null, otherInfo }
  }
}

export const getDueDateIUI = async (
  dateOfIUIProcedure: Date,
): Promise<DueDateInfoType> => {
  const params = [
    `method=${DueDateMethodAPI.IUI}`,
    `date=${dayjs(dateOfIUIProcedure).format(DISPLAY_DATE_FORMAT)}`,
  ].join('&')
  const path = `${PATHS.HEALTH_TOOL.GET_DUE_DATE_RESULT}?${params}`
  const otherInfo: OtherInfo = {
    method: DueDateCalculatorType.IUI + 1,
    insemination_date: dayjs(dateOfIUIProcedure).format(API_DATE_FORMAT),
    cycle_length_days: null,
    conception_date: null,
    ivf_transfer_date: null,
    days_transfer: null,
  }
  try {
    const { data } = await callApiNew(path)

    if (!data?.due_date && !data?.gestational_week?.[1]?.from) {
      return { startDate: null, dueDate: null, otherInfo }
    }

    return {
      startDate: dayjs(
        data.gestational_week[1].from,
        DISPLAY_DATE_FORMAT,
      ).toDate(),
      dueDate: dayjs(data.due_date, DISPLAY_DATE_FORMAT).toDate(),
      otherInfo,
    }
  } catch (error) {
    return { startDate: null, dueDate: null, otherInfo }
  }
}

export const getDueDateUltrasound = async (
  dateOfUltrasound: Date,
  additionalInfo: string,
): Promise<DueDateInfoType> => {
  const [a, b] = additionalInfo.split('|')
  const [crlNumber, msdNumber] = [Number(a), Number(b)]

  const params = [
    `method=${DueDateMethodAPI.ultrasound}`,
    `date=${dayjs(dateOfUltrasound).format(DISPLAY_DATE_FORMAT)}`,
    crlNumber ? `crl=${crlNumber}` : '',
    !crlNumber && msdNumber ? `msd=${msdNumber}` : '',
  ].join('&')
  const path = `${PATHS.HEALTH_TOOL.GET_DUE_DATE_RESULT}?${params}`
  const otherInfo: OtherInfo = {
    method: DueDateCalculatorType.ultrasound + 1,
    measurement_date: dayjs(dateOfUltrasound).format(API_DATE_FORMAT),
    crl_value: crlNumber,
    msd_value: msdNumber,
    insemination_date: null,
    cycle_length_days: null,
    conception_date: null,
    ivf_transfer_date: null,
    days_transfer: null,
  }
  try {
    const { data } = await callApiNew(path)

    if (!data?.due_date && !data?.gestational_week?.[1]?.from) {
      return { startDate: null, dueDate: null, otherInfo }
    }

    return {
      startDate: dayjs(
        data.gestational_week[1].from,
        DISPLAY_DATE_FORMAT,
      ).toDate(),
      dueDate: dayjs(data.due_date, DISPLAY_DATE_FORMAT).toDate(),
      otherInfo,
    }
  } catch (error) {
    return { startDate: null, dueDate: null, otherInfo }
  }
}

export const getDueDateConception = async (
  conceptionDate: Date,
): Promise<DueDateInfoType> => {
  const params = [
    `method=${DueDateMethodAPI.conception}`,
    `date=${dayjs(conceptionDate).format(DISPLAY_DATE_FORMAT)}`,
  ].join('&')
  const path = `${PATHS.HEALTH_TOOL.GET_DUE_DATE_RESULT}?${params}`
  const otherInfo: OtherInfo = {
    method: DueDateCalculatorType.conception + 1, // zero index -> 1 based index
    conception_date: dayjs(conceptionDate).format(API_DATE_FORMAT),
    insemination_date: null,
    measurement_date: null,
    first_day: null,
    cycle_length_days: null,
    ivf_transfer_date: null,
    days_transfer: null,
  }
  try {
    const { data } = await callApiNew(path)

    if (!data?.due_date && !data?.gestational_week?.[1]?.from) {
      return { startDate: null, dueDate: null, otherInfo }
    }

    return {
      startDate: dayjs(
        data.gestational_week[1].from,
        DISPLAY_DATE_FORMAT,
      ).toDate(),
      dueDate: dayjs(data.due_date, DISPLAY_DATE_FORMAT).toDate(),
      otherInfo,
    }
  } catch (error) {
    return { startDate: null, dueDate: null, otherInfo }
  }
}

export const getDueDateIVF = async (
  IVFDate: Date,
  additionalInfo: string,
): Promise<DueDateInfoType> => {
  const numDayTransfer = Number(additionalInfo)
  const params = [
    `method=${DueDateMethodAPI.IVF}`,
    `embryo_transfer_date=${numDayTransfer}`,
    `date=${dayjs(IVFDate).format(DISPLAY_DATE_FORMAT)}`,
  ].join('&')
  const path = `${PATHS.HEALTH_TOOL.GET_DUE_DATE_RESULT}?${params}`
  const otherInfo: OtherInfo = {
    method: DueDateCalculatorType.IVF + 1, // zero index -> 1 based index
    ivf_transfer_date: dayjs(IVFDate).format(API_DATE_FORMAT),
    measurement_date: null,
    insemination_date: null,
    days_transfer: numDayTransfer,
    conception_date: null,
    first_day: null,
    cycle_length_days: null,
  }

  try {
    const { data } = await callApiNew(path)

    if (!data?.due_date && !data?.gestational_week?.[1]?.from) {
      return { startDate: null, dueDate: null, otherInfo }
    }

    return {
      startDate: dayjs(
        data.gestational_week[1].from,
        DISPLAY_DATE_FORMAT,
      ).toDate(),
      dueDate: dayjs(data.due_date, DISPLAY_DATE_FORMAT).toDate(),
      otherInfo,
    }
  } catch (error) {
    return { startDate: null, dueDate: null, otherInfo }
  }
}

export const DateToInputValue = (date: Date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`
}

export const getDaysDifference = (firstDate: Date, secondDate: Date) => {
  const diffTime = Math.abs(secondDate.getTime() - firstDate.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

export const useDueDateStore = create<{
  isCalculating: boolean
  isRecalculate: boolean
  navResultTarget: string
  setNavResultTarget: (name: string) => void
  calculatorType: DueDateCalculatorType
  setCalculatorType: (newType: DueDateCalculatorType) => void
  setIsCalculating: (isCalculating: boolean) => void
  setIsRecalculate: (isRecalculate: boolean) => void
}>((set) => ({
  isCalculating: false,
  isRecalculate: false,
  navResultTarget: 'due_date_revealed',
  setNavResultTarget: (name) => set({ navResultTarget: name }),
  calculatorType: DueDateCalculatorType.lastPeriod,
  setCalculatorType: (calculatorType) => set(() => ({ calculatorType })),
  setIsCalculating: (isCalculating) => set(() => ({ isCalculating })),
  setIsRecalculate: (isRecalculate) => set(() => ({ isRecalculate })),
}))
